import React, { useEffect, useRef, useState } from 'react'

import {
    useDeleteCvV2ApplyMutation,
    useLazyGetApplyV2SocketQuery,
    usePostCvV2ApplyMutation,
    usePutApplyV2ParsedDataMutation,
} from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempBase64File, ApplyV2TempProfileCvData } from '@core/app/types/applyV2Type'
import { InfoIcon } from '@core/assets/icons/info_icon'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2AttachmentsUploader2 from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader/ApplyV2AttachmentsUploader2'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2InputRadio from '@core/components/ApplyDialogV2/ApplyV2InputRadio'
import UploadCvIcon from '@core/components/ApplyDialogV2/assets/UploadCvIcon'
import CvParsingDisabledNotice from '@core/components/CvParsingDisabledNotice'
import DefaultMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/DefaultMaskIcon'
import UnsupportedFileMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/UnsupportedFileMaskIcon'
import { getApplyFilePreviewAuth, useFilePreviewSession } from '@core/components/FilePreview'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import './ApplyV2CvStep2.scss'

const getCvParserRadioKey = (postId: number) => `cv_parser_radio_${postId}`
const getCvParserRadioFromStorage = (postId: number): boolean => {
    try {
        const stored = localStorage.getItem(getCvParserRadioKey(postId))
        return stored !== null ? JSON.parse(stored) : true
    } catch {
        return true
    }
}
const setCvParserRadioToStorage = (postId: number, value: boolean) => {
    try {
        localStorage.setItem(getCvParserRadioKey(postId), JSON.stringify(value))
    } catch {
        console.log('Silently fail if localStorage is not available')
    }
}

const baseClass = 'ApplyV2CvStep2'

function ApplyV2CvStep2() {
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        onNextStep,
        postProfileRefetch,
        sendWebSocketMessage,
        cvParserStatus,
        isWebSocketConnected,
        postData,
        initWebSocketConnection,
    } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const [files, setFiles] = useState<(ApplyV2TempBase64File | ApplyV2TempProfileCvData)[]>([])
    const { openPreview } = useFilePreview()
    const { openFilePreview } = useFilePreviewSession()
    const postId = postData?.ad?.AdID ?? 0
    const uid = postProfileData?.data?.uid ?? ''
    const [radio, setRadio] = useState<boolean>(() => {
        return postId > 0 ? getCvParserRadioFromStorage(postId) : true
    })

    const [postCvRequest, { isLoading: isCvLoading, error: cvError }] = usePostCvV2ApplyMutation()
    const [deleteCvRequest, { isLoading: isCvDeleteLoading, isError: isDeleteError }] = useDeleteCvV2ApplyMutation()
    const [putParsedDataRequest] = usePutApplyV2ParsedDataMutation()

    const LOADER_MS = 10000
    const [isLoaderVisible, setIsLoaderVisible] = useState(false)
    const [progress, setProgress] = useState(0)
    const [cvStateError, setCvStateError] = useState<string | null>(null)
    const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const progressTickerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const loaderStartRef = useRef<number>(0)

    const [isShowInput, setIsShowInput] = useState(true)
    const isCvParserAvailable = settingsData?.cvParserSettings?.isAvailable === true
    const isUploadInProgress = isCvParserAvailable ? isLoaderVisible : isCvLoading
    useEffect(() => {
        setIsShowInput(!(files.length || isCvDeleteLoading || isUploadInProgress))
    }, [files.length, isCvDeleteLoading, isUploadInProgress])

    const isCvRequired = settingsData?.steps?.cv?.required && settingsData?.steps?.cv?.visible

    useEffect(() => {
        if (!isLoaderVisible) {
            if (progressTickerRef.current) {
                clearTimeout(progressTickerRef.current)
                progressTickerRef.current = null
            }
            setProgress(0)
            return
        }

        if (progressTickerRef.current) return

        const tick = () => {
            progressTickerRef.current = setTimeout(() => {
                const elapsed = Date.now() - loaderStartRef.current
                const ratio = Math.min(0.999, elapsed / LOADER_MS)
                const target = Math.floor(98 * ratio) // cap at 98%
                setProgress((prev) => (target > prev ? target : prev))

                if (Date.now() - loaderStartRef.current < LOADER_MS) {
                    tick()
                } else {
                    // finish gracefully
                    setProgress(100)
                    clearTimeout(progressTickerRef.current as any)
                    progressTickerRef.current = null
                    setIsLoaderVisible(false)
                }
            }, 100)
        }

        tick()

        return () => {
            if (progressTickerRef.current) {
                clearTimeout(progressTickerRef.current)
                progressTickerRef.current = null
            }
        }
    }, [isLoaderVisible])

    const startSimpleLoader = () => {
        // clear previous timers
        if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current)
        if (progressTickerRef.current) {
            clearTimeout(progressTickerRef.current)
            progressTickerRef.current = null
        }
        loaderStartRef.current = Date.now()
        setProgress(0)
        setIsLoaderVisible(true)

        loaderTimeoutRef.current = setTimeout(() => {
            // hard stop after LOADER_MS (in case ticker cleanup missed)
            setIsLoaderVisible(false)
            loaderTimeoutRef.current = null
        }, LOADER_MS)
    }

    const cleanupLoader = () => {
        if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current)
        if (progressTickerRef.current) clearTimeout(progressTickerRef.current)
        loaderTimeoutRef.current = null
        progressTickerRef.current = null
        setIsLoaderVisible(false)
        setProgress(0)
    }

    const handleNext = async () => {
        onNextStep()
    }

    const deleteCvHandler = async () => {
        setCvStateError(null)
        try {
            const res = await deleteCvRequest({
                uid: postProfileData?.data?.uid ?? '',
            }).unwrap()
            setFiles([])
            console.log('Delete res', res)
        } catch (e) {}
    }

    const uploadCvHandler = async (cvs: ApplyV2TempBase64File[]) => {
        console.log('[ApplyV2CvStep2] uploadCvHandler called', {
            filesCount: cvs.length,
            cvParserAvailable: settingsData?.cvParserSettings?.isAvailable,
            radio,
            uid: postProfileData?.data?.uid,
        })

        setCvStateError(null)
        if (cvs.length > 1) {
            setCvStateError('max 1 file')
            return
        }
        const cv = cvs[0]
        const uid = postProfileData?.data?.uid

        if (!cv || !uid || !('base64' in cv)) {
            console.log('[ApplyV2CvStep2] No Cv or Uid', { cv: !!cv, uid, hasBase64: 'base64' in (cv || {}) })
            return
        }

        setFiles(cvs)
        if (isCvParserAvailable) {
            startSimpleLoader()
        }
        // console.log('[ApplyV2CvStep2] Starting CV upload...', { uid, fileName: cv.name })

        postCvRequest({
            uid: uid,
            body: {
                cv: {
                    name: cv.name,
                    ext: cv.ext,
                    base64: cv.base64,
                },
            },
        })
            .unwrap()
            .then((res) => {
                console.log('[ApplyV2CvStep2] CV upload successful', {
                    res,
                    timestamp: new Date().toISOString(),
                })

                console.log(res?.data?.cv)

                if (res?.data?.cv) {
                    console.log('DDDDDDDDDDDD')
                    setFiles([res?.data?.cv])
                }

                if (isCvParserAvailable && radio) {
                    console.log('[ApplyV2CvStep2] Initializing WebSocket connection after CV upload...', {
                        cvParserAvailable: settingsData?.cvParserSettings?.isAvailable,
                        radio,
                        isWebSocketConnected,
                    })
                    initWebSocketConnection()
                        .then(() => {
                            console.log('[ApplyV2CvStep2] WebSocket connected, sending messages for CV parsing...')
                            const subscribeResult = sendWebSocketMessage('subscribe')
                            console.log('[ApplyV2CvStep2] subscribe message result:', subscribeResult)
                            const cvParserResult = sendWebSocketMessage('cv-parser')
                            console.log('[ApplyV2CvStep2] cv-parser message result:', cvParserResult)
                        })
                        .catch((error) => {
                            console.error('[ApplyV2CvStep2] WebSocket initialization error', error)
                        })
                } else {
                    console.log('[ApplyV2CvStep2] Skipping WebSocket connection', {
                        cvParserAvailable: settingsData?.cvParserSettings?.isAvailable,
                        radio,
                        reason: !isCvParserAvailable ? 'CV parser not available' : 'Radio is false',
                    })
                }
            })
            .catch((e: any) => {
                cleanupLoader()
                console.error('[ApplyV2CvStep2] CV upload ERROR', e)
                if (e?.data?.errors && Array.isArray(e.data.errors)) {
                    const errs = e.data.errors.map((e: any) => e.msg).join(', ')
                    setCvStateError(errs || e.message)
                } else {
                    setCvStateError(e.message)
                }
            })
    }

    // console.log('Is isWebSocketConnected', isWebSocketConnected, cvParserStatus)

    useEffect(() => {
        if (postProfileData?.data?.useParsedData !== undefined) {
            setRadio(postProfileData.data.useParsedData)
        } else if (postId > 0) {
            const storedValue = getCvParserRadioFromStorage(postId)
            setRadio(storedValue)
        }
    }, [postId, postProfileData?.data?.useParsedData])

    useEffect(() => {
        if (postProfileData?.data?.cv) {
            setFiles([postProfileData.data.cv])
        } else {
            setFiles([])
        }
    }, [postProfileData?.data?.cv])

    useEffect(() => () => cleanupLoader(), [])

    useEffect(() => {
        if (isCvParserAvailable) return

        let timeout: ReturnType<typeof setTimeout> | null = null

        const tick = (delay: number) => {
            timeout = setTimeout(() => {
                setProgress((prev) => {
                    if (prev >= 98) return prev
                    let step = 1
                    if (prev >= 90) step = 1
                    const next = prev + step
                    const nextDelay = next < 60 ? delay : delay * 2
                    tick(nextDelay)
                    return next
                })
            }, delay)
        }

        if (isCvLoading) {
            setProgress(0)
            tick(50)
        } else {
            setProgress(0)
        }

        return () => {
            if (timeout) clearTimeout(timeout)
        }
    }, [isCvLoading, isCvParserAvailable])

    const filesErrors = files
        .map((f) => {
            return { ...f, error: 'errFF' }
        })
        .filter((f) => cvStateError)

    return (
        <div className={baseClass}>
            <ApplyV2AttachmentsUploader2
                maxFileSize={10}
                accept={getAcceptFilesString('CV')}
                files={files}
                onChange={uploadCvHandler}
                multipleCount={1}
                progressItems={
                    isCvParserAvailable
                        ? isLoaderVisible
                            ? [{ index: 0, progress }]
                            : []
                        : isCvLoading
                          ? [{ index: 0, progress }]
                          : []
                }
                onDelete={deleteCvHandler}
                errorMessage={cvStateError}
                isShowInput={isShowInput}
                loadingIds={isCvDeleteLoading ? files.map((f) => ('uid' in f ? f.uid : f.name)) : []}
                onFileClick={(f) => {
                    const previewAuth = getApplyFilePreviewAuth(f, uid)
                    if (previewAuth) {
                        openFilePreview(previewAuth)
                    } else {
                        openPreview(
                            {
                                Filename: f.name,
                                Ext: f.ext,
                                CandidateFileID: f.existingId,
                                downloadUrl: f.downloadUrl,
                                Size: f.size,
                                ...f,
                                uid: uid,
                                fileUid: f.uid,
                                type: f.type,
                            },
                            f.folder
                        )
                    }
                }}
            >
                <UploadCvIcon className={`${baseClass}__icon`} />
                <p
                    className={`${baseClass}__inner__text`}
                    style={{ fontSize: '14px', marginBottom: '2px', marginTop: '8px', fontWeight: 500 }}
                >
                    {getTitle('13198', 'Drop file here or click to upload')}
                </p>
                <p style={{ fontSize: '11px', color: '#999' }}>PDF / DOC / DOCX</p>
            </ApplyV2AttachmentsUploader2>

            {settingsData?.cvParserSettings?.isAvailable === false && (!!files.length || isUploadInProgress) && (
                <CvParsingDisabledNotice />
            )}

            {!files.length && isCvParserAvailable && (
                <div className={`${baseClass}__info`}>
                    <span className={`${baseClass}__info-icon`}>
                        <InfoIcon />
                    </span>
                    <span>
                        {getTitle(
                            '10696',
                            'Information from your CV will be automatically used to fill in your profile and the next steps. You can add, or edit details anytime.'
                        )}
                    </span>
                </div>
            )}

            {!!files.length && isCvParserAvailable && (
                <div className={`${baseClass}__description`}>
                    <p className={`${baseClass}__description--title`}>
                        {getTitle('8799', 'Would you like to automatically parse your CV?')}
                    </p>
                    <p className={`${baseClass}__description--par`}>
                        {getTitle(
                            '10696',
                            'Information from your CV will be automatically used to fill in your profile and the next steps. You can add, or edit details anytime.'
                        )}
                    </p>
                    <ApplyV2InputRadio
                        className={`${baseClass}__description--radio`}
                        value={radio}
                        onChange={async (f) => {
                            const newValue = !!f
                            setRadio(newValue)
                            if (postId > 0) setCvParserRadioToStorage(postId, newValue)
                            const uid = postProfileData?.data?.uid
                            if (uid) {
                                try {
                                    await putParsedDataRequest({ uid, useParsedData: newValue }).unwrap()
                                } catch (e) {
                                    console.error('Error updating useParsedData', e)
                                }
                            }
                        }}
                        options={[
                            { value: true, label: getTitle('128', 'Yes') },
                            { value: false, label: getTitle('8801', "No, I'll add information myself") },
                        ]}
                    />
                </div>
            )}
            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.cv?.required && (
                    <ApplyV2Button
                        variant="transparent"
                        className="ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip"
                        rightIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.25728 13.4386C5.01503 13.2125 5.00193 12.8328 5.22803 12.5906L9.5126 7.99997L5.22804 3.40936C5.00193 3.16711 5.01503 2.78744 5.25728 2.56134C5.49953 2.33524 5.8792 2.34833 6.1053 2.59058L10.772 7.59058C10.9871 7.82111 10.9871 8.17884 10.772 8.40936L6.1053 13.4094C5.8792 13.6516 5.49953 13.6647 5.25728 13.4386Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                        onClick={() => onNextStep()}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                <ApplyV2Button
                    type="submit"
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyV2__next--btn-submit`}
                    isLoading={false} // 🔒 no button spinner — only one loader on the uploader
                    isDisabled={isUploadInProgress || (isCvRequired && files.length < 1)}
                    onClick={handleNext}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </div>
    )
}

export default ApplyV2CvStep2
